/**
 * AddonsShopScreen — Boutique standalone d'add-ons
 *
 * Accessible depuis Profil → "Boutique" (V5.6 bundle v5).
 * 3 sections : Try-Ons, AI Scans, Wardrobe (Boost + Plus/Max).
 *
 * v1.12.608 — Thème Mytenue (cream #F4ECDD / navy #1a1f36 / gold #D4AF37).
 */
import React, { useEffect, useState, useCallback } from 'react';
import {
  View, Text, ScrollView, TouchableOpacity, StyleSheet,
  ActivityIndicator, SafeAreaView, StatusBar, Platform, Alert,
} from 'react-native';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../i18n';
import BackButton from '../components/BackButton';
import { showAppAlert } from '../components/AppDialog';
import { openLegalUrl } from '../utils/openLegalUrl';
import { AWS_CONFIG } from '../config/aws_config';
import * as SecureStore from '../utils/secureStore';
import { ADDONS, formatAddonLine } from '../config/addonsConfig';
import {
  startAddonCheckout,
  startWardrobeSubscriptionCheckout,
  cancelAddonSubscription,
  fetchMyWardrobeSubscriptions,
} from '../services/StripeService';
import ThemedRefreshControl from '../components/ThemedRefreshControl';

const COLORS = {
  bg:       '#F4ECDD',
  card:     '#FFFFFF',
  navy:     '#1a1f36',
  gold:     '#D4AF37',
  goldLight:'#FFF8E1',
  text:     '#1A1A1A',
  textLight:'#666',
  border:   '#E5DECE',
  green:    '#2E7D32',
  red:      '#D32F2F',
};

export default function AddonsShopScreen({ navigation }) {
  const { user, getValidToken } = useAuth();
  const { t, lang } = useI18n();
  const isFR = !lang || lang.startsWith('fr');

  const [balance, setBalance] = useState(null);
  const [activeSubs, setActiveSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [buyingId, setBuyingId] = useState(null);
  const [cancellingId, setCancellingId] = useState(null);
  // v1.12.895 — Historique des achats add-ons (paginé)
  const [history, setHistory] = useState([]);
  const [historyHasMore, setHistoryHasMore] = useState(false);
  const [historyOffset, setHistoryOffset] = useState(0);
  const [historyLoading, setHistoryLoading] = useState(false);
  const HISTORY_PAGE = 5;

  const fetchHistory = useCallback(async (offset = 0, append = false) => {
    try {
      setHistoryLoading(true);
      const token = await SecureStore.getItemAsync('outfit_access');
      if (!token) { setHistoryLoading(false); return; }
      const res = await fetch(
        `${AWS_CONFIG.apiUrl}/addons/history?limit=${HISTORY_PAGE}&offset=${offset}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) { setHistoryLoading(false); return; }
      const data = await res.json();
      setHistory(prev => append ? [...prev, ...(data.items || [])] : (data.items || []));
      setHistoryHasMore(!!data.hasMore);
      setHistoryOffset(offset + (data.items?.length || 0));
    } catch (e) { console.warn('[Shop] history fetch failed:', e?.message); }
    finally { setHistoryLoading(false); }
  }, []);

  const fetchData = useCallback(async () => {
    try {
      const token = await SecureStore.getItemAsync('outfit_access');
      if (!token) { setLoading(false); return; }
      const [balanceRes, subsRes] = await Promise.all([
        fetch(`${AWS_CONFIG.apiUrl}/addons/balance`, { headers: { Authorization: `Bearer ${token}` } })
          .then(r => r.ok ? r.json() : null).catch(() => null),
        fetchMyWardrobeSubscriptions().catch(() => []),
      ]);
      if (balanceRes) setBalance(balanceRes);
      if (Array.isArray(subsRes)) setActiveSubs(subsRes);
    } catch (e) { console.warn('[Shop] fetch failed:', e?.message); }
    finally { setLoading(false); setRefreshing(false); }
  }, []);

  useEffect(() => { fetchData(); fetchHistory(0, false); }, [fetchData, fetchHistory]);
  const onRefresh = useCallback(() => { setRefreshing(true); fetchData(); fetchHistory(0, false); }, [fetchData, fetchHistory]);

  const tier = balance?.wardrobeQuota?.tier || user?.tier || 'free';
  const isPaid = tier !== 'free';

  const onBuy = async (addon) => {
    if (buyingId) return;
    setBuyingId(addon.id);
    try {
      if (Platform.OS !== 'web') {
        // v1.12.952 — iOS/Android : achat via Apple/Google IAP (RevenueCat).
        // JAMAIS de redirection vers le web (règle Apple 3.1.1). Avant : on
        // renvoyait vers www.mytenue.com -> rejet App Review 2.1(b)/3.1.1.
        const { purchaseAddon } = require('../services/IAPService');
        const result = await purchaseAddon(addon.id, { id: user?.id });
        setBuyingId(null);
        if (result?.cancelled) return;
        if (result?.success) {
          showAppAlert(
            isFR ? '✅ Achat confirmé' : '✅ Purchase confirmed',
            isFR ? 'Votre achat est validé. Le solde sera mis à jour sous peu.'
                 : 'Your purchase is confirmed. Your balance will update shortly.'
          );
          fetchData();
          fetchHistory(0, false);
          // v1.12.969 — l'achat est crédité + l'historique inséré par le webhook
          // RevenueCat (asynchrone, léger délai). On re-fetch solde + historique
          // plusieurs fois pour capter la ligne dès qu'elle arrive (sinon l'achat
          // n'apparaît pas dans l'historique tant qu'on ne recharge pas l'écran).
          [1500, 4000, 8000].forEach(ms => setTimeout(() => { fetchData(); fetchHistory(0, false); }, ms));
          // v1.12.957 — notifier les autres écrans (Profil, Dressing) pour qu'ils
          // rafraîchissent solde/limites sans redémarrage de l'app.
          try { require('react-native').DeviceEventEmitter.emit('addon_balance_changed'); } catch {}
        }
        return;
      }
      // v1.12.853 — Application automatique Crédit Mytenue
      const { purchaseAddonWithCredits, purchaseWardrobeSubscriptionWithCredits } = require('../services/CreditAwarePurchase');
      const ctx = { addonId: addon.id, user, getValidToken, lang: isFR ? 'fr' : 'en' };
      if (addon.consumability === 'subscription') {
        await purchaseWardrobeSubscriptionWithCredits(ctx);
      } else {
        await purchaseAddonWithCredits(ctx);
      }
    } catch (e) {
      setBuyingId(null);
      // v1.12.952 — message neutre (jamais de mention web ni de détail technique RevenueCat)
      const noPkg = /introuvable|not found|no package/i.test(e?.message || '');
      showAppAlert(
        isFR ? 'Achat indisponible' : 'Purchase unavailable',
        noPkg
          ? (isFR ? 'Cet article sera bientôt disponible à l\'achat. Réessayez plus tard.'
                  : 'This item will be available for purchase soon. Please try again later.')
          : (e?.message || (isFR ? 'Erreur paiement' : 'Payment error'))
      );
    }
  };

  const onCancel = async (subId, addonId) => {
    if (cancellingId) return;
    const cfg = ADDONS[addonId];
    const label = cfg ? (isFR ? cfg.labelFr : cfg.labelEn) : addonId;
    const confirmed = Platform.OS === 'web'
      ? (typeof window !== 'undefined' && window.confirm(
          isFR
            ? `Résilier "${label}" ?\n\nVotre abonnement restera actif jusqu'à la fin de la période en cours, puis vos slots bonus seront retirés.`
            : `Cancel "${label}"?\n\nYour subscription stays active until end of current period, then bonus slots will be removed.`
        ))
      : await new Promise(resolve => {
          Alert.alert(
            isFR ? `Résilier "${label}" ?` : `Cancel "${label}"?`,
            isFR
              ? 'Actif jusqu\'à la fin de la période en cours.'
              : 'Active until end of current period.',
            [
              { text: isFR ? 'Annuler' : 'Cancel', style: 'cancel', onPress: () => resolve(false) },
              { text: isFR ? 'Résilier' : 'Cancel', style: 'destructive', onPress: () => resolve(true) },
            ]
          );
        });
    if (!confirmed) return;
    setCancellingId(subId);
    try {
      await cancelAddonSubscription(subId);
      showAppAlert(
        isFR ? '✓ Résiliation programmée' : '✓ Cancellation scheduled',
        isFR ? `"${label}" sera résilié à la fin de la période en cours.`
              : `"${label}" will end at the current period close.`
      );
      fetchData();
    } catch (e) {
      showAppAlert(
        isFR ? 'Erreur' : 'Error',
        e?.message || 'Failed'
      );
    } finally {
      setCancellingId(null);
    }
  };

  if (loading) {
    return (
      <SafeAreaView style={[styles.container, { backgroundColor: COLORS.bg }]}>
        <StatusBar barStyle="dark-content" backgroundColor={COLORS.bg} />
        <View style={styles.headerRow}>
          <BackButton onPress={() => navigation.goBack()} />
          <Text style={[styles.headerTitle, { color: COLORS.navy }]}>
            {isFR ? 'Boutique' : 'Shop'}
          </Text>
          <View style={{ width: 40 }} />
        </View>
        <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center' }}>
          <ActivityIndicator size="large" color={COLORS.gold} />
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: COLORS.bg }]}>
      <StatusBar barStyle="dark-content" backgroundColor={COLORS.bg} />
      <View style={styles.headerRow}>
        <BackButton onPress={() => navigation.goBack()} />
        <Text style={[styles.headerTitle, { color: COLORS.navy }]}>
          {isFR ? '🛍️ Boutique' : '🛍️ Shop'}
        </Text>
        <View style={{ width: 40 }} />
      </View>

      <ScrollView
        contentContainerStyle={{ padding: 16, paddingBottom: 40 }}
        refreshControl={<ThemedRefreshControl refreshing={refreshing} onRefresh={onRefresh} />}
      >
        {/* ═══ Solde actuel ═══ */}
        <View style={styles.balanceCard}>
          <Text style={styles.balanceTitle}>
            {isFR ? '📊 Mon solde' : '📊 My balance'}
          </Text>
          <View style={styles.balanceRow}>
            <BalanceItem emoji="🪞" label="Try-Ons" value={balance?.tryons ?? 0} />
            <BalanceItem
              emoji="✨"
              label={isFR ? 'AI scans' : 'AI scans'}
              value={balance?.scans ?? 0}
            />
            <BalanceItem
              emoji="👔"
              label={isFR ? 'Dressing' : 'Wardrobe'}
              value={balance?.wardrobeQuota?.totalAvailable ?? 0}
              suffix={balance?.wardrobeQuota?.bonusQuota > 0 ? `(+${balance.wardrobeQuota.bonusQuota})` : ''}
            />
          </View>
          <Text style={styles.balanceSubtext}>
            {isFR ? `Plan actuel : ${tier.charAt(0).toUpperCase() + tier.slice(1)}` : `Current plan: ${tier.charAt(0).toUpperCase() + tier.slice(1)}`}
          </Text>
        </View>

        {/* ═══ Abonnements wardrobe actifs (Plus / Max) ═══ */}
        {activeSubs.length > 0 && (
          <View style={styles.sectionCard}>
            <Text style={styles.sectionTitle}>
              {isFR ? '📅 Mes abonnements actifs' : '📅 My active subscriptions'}
            </Text>
            {activeSubs.map(sub => {
              const cfg = ADDONS[sub.addon_id];
              if (!cfg) return null;
              const label = isFR ? cfg.labelFr : cfg.labelEn;
              const periodEnd = sub.current_period_end ? new Date(sub.current_period_end) : null;
              const periodEndStr = periodEnd ? periodEnd.toLocaleDateString(lang || 'fr-FR') : '?';
              const isCancelling = cancellingId === sub.id;
              return (
                <View key={sub.id} style={styles.subRow}>
                  <View style={{ flex: 1 }}>
                    <Text style={styles.subLabel}>👔 {label} (+{cfg.quantity} slots)</Text>
                    <Text style={styles.subPeriod}>
                      {sub.cancel_at_period_end
                        ? (isFR ? `Se termine le ${periodEndStr}` : `Ends ${periodEndStr}`)
                        : (isFR ? `Renouvellement le ${periodEndStr}` : `Renews ${periodEndStr}`)}
                    </Text>
                  </View>
                  {!sub.cancel_at_period_end && (
                    <TouchableOpacity
                      onPress={() => onCancel(sub.id, sub.addon_id)}
                      disabled={isCancelling}
                      style={[styles.cancelBtn, isCancelling && { opacity: 0.5 }]}
                    >
                      {isCancelling
                        ? <ActivityIndicator size="small" color={COLORS.red} />
                        : <Text style={styles.cancelBtnText}>{isFR ? 'Résilier' : 'Cancel'}</Text>}
                    </TouchableOpacity>
                  )}
                </View>
              );
            })}
          </View>
        )}

        {/* ═══ Sections d'achat ═══ */}
        <ShopSection
          title={isFR ? '🪞 Try-Ons' : '🪞 Try-Ons'}
          desc={isFR ? 'Essais virtuels supplémentaires' : 'Extra virtual try-ons'}
          addons={getAddonsByType('tryons', tier)}
          onBuy={onBuy}
          buyingId={buyingId}
          isFR={isFR}
          lang={lang}
        />
        <ShopSection
          title={isFR ? '✨ Analyses IA' : '✨ AI Analyses'}
          desc={isFR ? 'Boost de scans IA pour analyser plus d\'articles' : 'AI scans boost'}
          addons={getAddonsByType('scans', tier)}
          onBuy={onBuy}
          buyingId={buyingId}
          isFR={isFR}
          lang={lang}
        />
        <ShopSection
          title={isFR ? '👔 Dressing étendu' : '👔 Extended Wardrobe'}
          desc={isFR ? 'Plus de slots pour stocker vos pièces' : 'More slots to store your items'}
          addons={getWardrobeAddons(tier)}
          onBuy={onBuy}
          buyingId={buyingId}
          isFR={isFR}
          lang={lang}
          showUpsellToBasic={tier === 'free'}
          onUpsell={() => navigation.navigate('Profile')}
        />

        {/* Historique des achats — v1.12.895 */}
        <HistorySection
          history={history}
          hasMore={historyHasMore}
          onLoadMore={() => fetchHistory(historyOffset, true)}
          loading={historyLoading}
          isFR={isFR}
        />

        {/* Footer info */}
        <View style={styles.footerInfo}>
          <Text style={styles.footerInfoText}>
            {isFR
              ? 'ℹ️ Try-Ons et AI scans : valables 24h à 30j selon le pack. Slots wardrobe : permanents tant que votre abonnement Basic ou supérieur est actif (mis en pause si vous repassez en Free, jamais supprimés).'
              : 'ℹ️ Try-Ons and AI scans: valid 24h to 30d depending on pack. Wardrobe slots: permanent as long as Basic or higher is active (paused if you return to Free, never deleted).'}
          </Text>
        </View>

        {/* v1.12.953 — Divulgation paiement IAP (Apple Guideline 3.1.2) :
            mention auto-renouvellement pour les abonnements wardrobe + achats
            uniques pour les consommables + liens EULA/Confidentialité. */}
        {Platform.OS !== 'web' && (
          <View style={styles.footerInfo}>
            <Text style={styles.footerInfoText}>
              {isFR
                ? 'Wardrobe +100 et Wardrobe +250 sont des abonnements auto-renouvelables (0,99 €/mois et 2,49 €/mois). Le paiement est débité lors de la confirmation. Le renouvellement est automatique à la même période et au même tarif, sauf annulation au moins 24 h avant la fin de la période en cours. Gérez ou résiliez votre abonnement dans les réglages de votre compte Apple ID. Les packs Try-On et AI scans sont des achats uniques, non renouvelables.'
                : 'Wardrobe +100 and Wardrobe +250 are auto-renewable subscriptions (€0.99/month and €2.49/month). Payment is charged upon confirmation of purchase. They renew automatically at the same price and duration unless canceled at least 24 hours before the end of the current period. Manage or cancel your subscription anytime in your Apple ID account settings. Try-On and AI scan packs are one-time, non-renewable purchases.'}
            </Text>
            <View style={{ flexDirection: 'row', justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap', marginTop: 8 }}>
              <Text onPress={() => openLegalUrl('terms', lang)} style={{ fontSize: 11, color: '#D4AF37', textDecorationLine: 'underline' }}>
                {isFR ? "Conditions d'utilisation (EULA)" : 'Terms of Use (EULA)'}
              </Text>
              <Text style={{ fontSize: 11, color: '#9aa' }}>{'    ·    '}</Text>
              <Text onPress={() => openLegalUrl('privacy', lang)} style={{ fontSize: 11, color: '#D4AF37', textDecorationLine: 'underline' }}>
                {isFR ? 'Politique de confidentialité' : 'Privacy Policy'}
              </Text>
            </View>
          </View>
        )}
      </ScrollView>
    </SafeAreaView>
  );
}

// v1.12.895 — Historique des achats d'add-ons
function HistorySection({ history, hasMore, onLoadMore, loading, isFR }) {
  if (!history || history.length === 0) {
    return null;
  }
  const fmtDate = (iso) => {
    if (!iso) return isFR ? 'Aucune expiration' : 'No expiration';
    try {
      const d = new Date(iso);
      return d.toLocaleDateString(isFR ? 'fr-FR' : 'en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch { return '—'; }
  };
  const statusBadge = (item) => {
    const map = {
      active:   { bg: '#E8F5E9', fg: '#2E7D32', label: isFR ? 'Actif' : 'Active' },
      expired:  { bg: '#FFEBEE', fg: '#C62828', label: isFR ? 'Expiré' : 'Expired' },
      consumed: { bg: '#F5F5F5', fg: '#616161', label: isFR ? 'Épuisé' : 'Used up' },
      refunded: { bg: '#FFF3E0', fg: '#E65100', label: isFR ? 'Remboursé' : 'Refunded' },
    };
    return map[item.statusEffective] || map.active;
  };
  return (
    <View style={styles.sectionCard}>
      <Text style={styles.sectionTitle}>{isFR ? '📋 Historique d\'achats' : '📋 Purchase history'}</Text>
      <Text style={styles.sectionDesc}>
        {isFR ? 'Vos add-ons achetés, leur usage et date d\'expiration' : 'Your purchased add-ons, usage and expiry date'}
      </Text>
      {history.map((it, idx) => {
        const badge = statusBadge(it);
        const pct = Math.max(0, Math.min(100, it.usagePct || 0));
        // v1.12.896 — Fix React err #31 : label peut venir comme {fr,en} ou string
        let displayLabel = it.addon_id;
        const rawLabel = isFR ? it.labelFr : it.labelEn;
        if (rawLabel) {
          displayLabel = String(rawLabel);
        } else if (it.label) {
          if (typeof it.label === 'string') displayLabel = it.label;
          else if (typeof it.label === 'object') displayLabel = String(it.label[isFR ? 'fr' : 'en'] || it.label.fr || it.label.en || it.addon_id);
        }
        return (
          <View key={it.id || idx} style={[
            styles.historyRow,
            it.isExpired && { opacity: 0.55 },
          ]}>
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <View style={{ flex: 1, paddingRight: 8 }}>
                <Text style={styles.historyLabel} numberOfLines={1}>
                  {displayLabel}
                </Text>
                <Text style={styles.historyMeta}>
                  {fmtDate(it.purchased_at)} · {Number(it.price_eur || 0).toFixed(2)} €
                </Text>
              </View>
              <View style={[styles.historyBadge, { backgroundColor: badge.bg }]}>
                <Text style={[styles.historyBadgeText, { color: badge.fg }]}>{badge.label}</Text>
              </View>
            </View>

            {/* Progression usage */}
            {it.quantity > 0 && (
              <View style={{ marginTop: 8 }}>
                <View style={styles.progressBg}>
                  <View style={[
                    styles.progressFg,
                    { width: `${pct}%`, backgroundColor: pct >= 100 ? '#9E9E9E' : (pct >= 80 ? '#FB8C00' : COLORS.gold) },
                  ]} />
                </View>
                <Text style={styles.historyUsage}>
                  {isFR
                    ? `${it.used} / ${it.quantity} utilisé${it.used > 1 ? 's' : ''} (reste ${it.remaining})`
                    : `${it.used} / ${it.quantity} used (${it.remaining} left)`}
                </Text>
              </View>
            )}

            {/* Expiration */}
            <Text style={styles.historyExpiry}>
              {it.expires_at
                ? (it.isExpired
                    ? `${isFR ? 'Expiré le' : 'Expired on'} ${fmtDate(it.expires_at)}`
                    : `${isFR ? 'Expire le' : 'Expires on'} ${fmtDate(it.expires_at)}`)
                : (isFR ? 'Aucune date d\'expiration' : 'No expiration date')}
            </Text>
          </View>
        );
      })}

      {hasMore && (
        <TouchableOpacity
          onPress={onLoadMore}
          disabled={loading}
          style={styles.historyMoreBtn}
          activeOpacity={0.7}
        >
          {loading
            ? <ActivityIndicator size="small" color={COLORS.navy} />
            : <Text style={styles.historyMoreText}>{isFR ? 'Afficher plus ↓' : 'Show more ↓'}</Text>}
        </TouchableOpacity>
      )}
    </View>
  );
}

function BalanceItem({ emoji, label, value, suffix = '' }) {
  return (
    <View style={{ alignItems: 'center', flex: 1 }}>
      <Text style={{ fontSize: 24 }}>{emoji}</Text>
      <Text style={{ fontSize: 22, fontWeight: '700', color: COLORS.gold, marginTop: 4 }}>
        {value}{suffix ? ` ${suffix}` : ''}
      </Text>
      <Text style={{ fontSize: 11, color: COLORS.textLight, marginTop: 2 }}>{label}</Text>
    </View>
  );
}

function ShopSection({ title, desc, addons, onBuy, buyingId, isFR, lang, showUpsellToBasic, onUpsell }) {
  if (!addons || addons.length === 0) return null;
  return (
    <View style={styles.sectionCard}>
      <Text style={styles.sectionTitle}>{title}</Text>
      <Text style={styles.sectionDesc}>{desc}</Text>
      <View style={{ gap: 8 }}>
        {addons.map(addon => {
          const isPopular = addon.badge === 'popular';
          const isBest = addon.badge === 'best_value';
          const isLocked = addon._locked === true;
          const isBuying = buyingId === addon.id;
          const line = formatAddonLine(addon, lang);
          return (
            <TouchableOpacity
              key={addon.id}
              onPress={() => isLocked ? onUpsell?.() : onBuy(addon)}
              disabled={!!buyingId}
              activeOpacity={0.85}
              style={{
                flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
                backgroundColor: isLocked ? '#F0F0F0' : (isBest ? COLORS.gold : (isPopular ? COLORS.goldLight : '#FAFAFA')),
                borderWidth: isBest && !isLocked ? 0 : 1,
                borderColor: isLocked ? '#D0D0D0' : (isPopular ? COLORS.gold : COLORS.border),
                borderRadius: 10,
                paddingVertical: 14, paddingHorizontal: 16,
                opacity: isLocked ? 0.7 : (buyingId && !isBuying ? 0.5 : 1),
              }}
            >
              <View style={{ flex: 1 }}>
                <Text style={{ fontSize: 14, fontWeight: '600', color: isLocked ? '#888' : (isBest ? '#000' : COLORS.text) }}>
                  {isLocked ? '🔒 ' : ''}{line}
                </Text>
                {!!addon.ttlDays && (
                  <Text style={{ fontSize: 11, color: isBest ? '#333' : COLORS.textLight, marginTop: 2 }}>
                    {isFR ? `Valable ${addon.ttlDays} ${addon.ttlDays > 1 ? 'jours' : 'jour'}` : `Valid for ${addon.ttlDays} day${addon.ttlDays > 1 ? 's' : ''}`}
                  </Text>
                )}
                {addon.recurringInterval === 'month' && (
                  <Text style={{ fontSize: 11, color: isBest ? '#333' : COLORS.textLight, marginTop: 2 }}>
                    {isFR ? 'Mensuel · résiliable à tout moment' : 'Monthly · cancel anytime'}
                  </Text>
                )}
                {!addon.ttlDays && !addon.recurringInterval && (
                  <Text style={{ fontSize: 11, color: isBest ? '#333' : COLORS.textLight, marginTop: 2 }}>
                    {isFR ? 'Permanent' : 'Permanent'}
                  </Text>
                )}
              </View>
              {isBuying ? (
                <ActivityIndicator size="small" color={isBest ? '#000' : COLORS.gold} />
              ) : isLocked ? (
                <View style={{ paddingHorizontal: 8, paddingVertical: 3, borderRadius: 12, backgroundColor: '#666' }}>
                  <Text style={{ fontSize: 10, fontWeight: '700', color: '#FFF', letterSpacing: 0.5 }}>
                    {isFR ? 'BASIC+ REQUIS' : 'BASIC+ REQUIRED'}
                  </Text>
                </View>
              ) : (isPopular || isBest) ? (
                <View style={{ paddingHorizontal: 8, paddingVertical: 3, borderRadius: 12, backgroundColor: isBest ? '#000' : COLORS.gold }}>
                  <Text style={{ fontSize: 10, fontWeight: '700', color: '#FFF', letterSpacing: 0.5 }}>
                    {isBest ? (isFR ? 'MEILLEURE OFFRE' : 'BEST VALUE') : (isFR ? 'POPULAIRE' : 'POPULAR')}
                  </Text>
                </View>
              ) : null}
            </TouchableOpacity>
          );
        })}
      </View>
      {showUpsellToBasic && (
        <View style={styles.upsellCard}>
          <Text style={styles.upsellTitle}>
            {isFR ? '⭐ Débloquez les abonnements wardrobe' : '⭐ Unlock wardrobe subscriptions'}
          </Text>
          <Text style={styles.upsellDesc}>
            {isFR
              ? 'Les abonnements Espace Plus (+100 slots) et Espace Max (+250 slots) nécessitent Basic. Le Boost +50 reste accessible.'
              : 'Space Plus (+100 slots) and Space Max (+250 slots) require Basic. Boost +50 stays available.'}
          </Text>
          <TouchableOpacity onPress={onUpsell} style={styles.upsellBtn} activeOpacity={0.85}>
            <Text style={styles.upsellBtnText}>
              {isFR ? 'Voir les abonnements' : 'See plans'}
            </Text>
          </TouchableOpacity>
        </View>
      )}
    </View>
  );
}

// Helpers
function getAddonsByType(type, tier) {
  return Object.values(ADDONS)
    .filter(a => a.type === type && a.eligibleTiers.includes(tier))
    .sort((a, b) => a.quantity - b.quantity);
}

function getWardrobeAddons(tier) {
  return Object.values(ADDONS)
    .filter(a => a.type === 'wardrobe_boost' || a.type === 'wardrobe_subscription')
    .map(a => ({ ...a, _locked: !a.eligibleTiers.includes(tier) }))
    .sort((a, b) => a.quantity - b.quantity);
}

const styles = StyleSheet.create({
  container: { flex: 1 },
  headerRow: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    paddingHorizontal: 8, paddingVertical: 8,
    paddingTop: Platform.OS === 'android' ? 16 : 8,
  },
  headerTitle: { fontSize: 18, fontWeight: '700', letterSpacing: -0.3 },
  balanceCard: {
    backgroundColor: COLORS.card,
    borderRadius: 14, padding: 18, marginBottom: 16,
    borderWidth: 1, borderColor: COLORS.border,
    shadowColor: COLORS.navy, shadowOpacity: 0.05, shadowRadius: 8, shadowOffset: { width: 0, height: 2 },
  },
  balanceTitle: { fontSize: 16, fontWeight: '700', color: COLORS.navy, marginBottom: 4 },
  balanceRow: { flexDirection: 'row', justifyContent: 'space-around', marginTop: 12, marginBottom: 8 },
  balanceSubtext: { fontSize: 11, textAlign: 'center', marginTop: 4, color: COLORS.textLight },
  sectionCard: {
    backgroundColor: COLORS.card,
    borderRadius: 14, padding: 18, marginBottom: 16,
    borderWidth: 1, borderColor: COLORS.border,
    shadowColor: COLORS.navy, shadowOpacity: 0.05, shadowRadius: 8, shadowOffset: { width: 0, height: 2 },
  },
  sectionTitle: { fontSize: 16, fontWeight: '700', color: COLORS.navy, marginBottom: 4 },
  sectionDesc: { fontSize: 12, color: COLORS.textLight, marginBottom: 14 },
  subRow: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    paddingVertical: 10, borderBottomWidth: 1, borderBottomColor: '#F5F0E5',
  },
  subLabel: { fontSize: 14, fontWeight: '600', color: COLORS.text },
  subPeriod: { fontSize: 11, color: COLORS.textLight, marginTop: 2 },
  cancelBtn: { paddingHorizontal: 12, paddingVertical: 6, borderWidth: 1, borderColor: COLORS.red, borderRadius: 8 },
  cancelBtnText: { fontSize: 12, color: COLORS.red, fontWeight: '600' },
  upsellCard: {
    marginTop: 14, padding: 14, borderRadius: 10,
    backgroundColor: COLORS.goldLight, borderWidth: 1, borderColor: COLORS.gold,
  },
  upsellTitle: { fontSize: 13, fontWeight: '700', color: COLORS.navy, marginBottom: 4 },
  upsellDesc: { fontSize: 12, color: COLORS.text, lineHeight: 17, marginBottom: 10 },
  upsellBtn: {
    backgroundColor: COLORS.navy, borderRadius: 8, paddingVertical: 10, paddingHorizontal: 14,
    alignItems: 'center',
  },
  upsellBtnText: { fontSize: 13, fontWeight: '700', color: COLORS.gold, letterSpacing: 0.5 },
  footerInfo: {
    backgroundColor: COLORS.card, borderRadius: 10, padding: 14,
    borderWidth: 1, borderColor: COLORS.border, marginTop: 8,
  },
  footerInfoText: { fontSize: 11, color: COLORS.textLight, lineHeight: 17 },
  // v1.12.895 — Historique
  historyRow: {
    paddingVertical: 12,
    borderBottomWidth: 1, borderBottomColor: '#F5F0E5',
  },
  historyLabel: { fontSize: 14, fontWeight: '600', color: COLORS.text },
  historyMeta: { fontSize: 11, color: COLORS.textLight, marginTop: 2 },
  historyBadge: {
    paddingHorizontal: 8, paddingVertical: 3, borderRadius: 999,
  },
  historyBadgeText: { fontSize: 10, fontWeight: '700', letterSpacing: 0.4 },
  progressBg: {
    height: 6, backgroundColor: '#F5F0E5', borderRadius: 999, overflow: 'hidden',
  },
  progressFg: { height: '100%', borderRadius: 999 },
  historyUsage: { fontSize: 11, color: COLORS.textLight, marginTop: 4 },
  historyExpiry: { fontSize: 11, color: COLORS.textLight, marginTop: 4, fontStyle: 'italic' },
  historyMoreBtn: {
    marginTop: 12, paddingVertical: 10, borderRadius: 8,
    backgroundColor: COLORS.bg, alignItems: 'center', justifyContent: 'center',
    borderWidth: 1, borderColor: COLORS.border,
  },
  historyMoreText: { fontSize: 13, fontWeight: '600', color: COLORS.navy },
});
